import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type PagedResult,
  type ProjectFolderDetail,
  type ProjectFolderListItem,
  type ProjectFolderWrite,
  type StructureDetail,
} from '../lib/api';
import { structureDisplayName } from '../lib/structureReport';

/**
 * Mirrors the legacy `structure/addtoproject.blade.php` page (route
 * `addtoproject`). Lists the user's project folders with a shared
 * "Quantity To Add" input and an "Add To Project" action per folder, plus a
 * "Create New Project" flow that creates a folder containing this structure.
 */
export function AddStructureToProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [quantity, setQuantity] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [added, setAdded] = useState<{
    folderId: number;
    folderName: string;
    quantity: number;
  } | null>(null);

  const structureQuery = useQuery({
    queryKey: ['structure', id],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${id}`),
    enabled: !!id,
  });

  const foldersQuery = useQuery({
    queryKey: ['project-folders', 'all'],
    queryFn: () =>
      api.get<PagedResult<ProjectFolderListItem>>('/api/v1/project-folders?pageSize=200'),
  });

  const s = structureQuery.data;

  const addMutation = useMutation({
    mutationFn: (folderId: number) =>
      api.post<void>(`/api/v1/project-folders/${folderId}/structures`, {
        structureId: s!.id,
        quantity: Math.max(1, quantity),
      }),
    onSuccess: (_data, folderId) => {
      const folder = foldersQuery.data?.items.find((f) => f.id === folderId);
      queryClient.invalidateQueries({ queryKey: ['project-folders', 'all'] });
      setAdded({
        folderId,
        folderName: folder?.name ?? 'project',
        quantity: Math.max(1, quantity),
      });
    },
    onError: (err) =>
      setFeedback(err instanceof Error ? err.message : 'Could not add to project.'),
  });

  const createMutation = useMutation({
    mutationFn: async (form: ProjectFolderWrite) => {
      const folder = await api.post<ProjectFolderDetail>('/api/v1/project-folders', form);
      await api.post<void>(`/api/v1/project-folders/${folder.id}/structures`, {
        structureId: s!.id,
        quantity: Math.max(1, quantity),
      });
      return folder;
    },
    onSuccess: (folder) => {
      navigate(`/admin/projects/${folder.id}/summary`);
    },
  });

  if (structureQuery.isLoading) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-lg-6 text-center text-muted">Loading…</div>
        </div>
      </div>
    );
  }

  if (structureQuery.isError || !s) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-lg-6">
            <div className="alert alert-danger">Could not load structure {id}.</div>
            <Link to="/" className="btn btn-sm btn-outline-secondary">
              ← Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const styleNumber = s.partNumber ?? s.foundryPartNumber ?? '—';
  const folders = foldersQuery.data?.items ?? [];

  if (added) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1">
          <div className="col col-xl-12 col-lg-12 text-center">
            <h1>
              {structureDisplayName(s)} Has Successfully Been Added To {added.folderName}
            </h1>
            <p>Quantity: {added.quantity}</p>
          </div>
          <div className="col-12 offset-xl-3 offset-lg-1 col-xl-6 col-lg-10 text-center">
            <div className="row mt-lg-3 mt-1">
              <div className="col-12 col-md-6 col-xl-6 d-grid">
                <Link
                  to={`/structurereport/view/${s.id}`}
                  className="btn btn-outline-secondary m-2"
                >
                  <img className="btn-img me-1" src="/images/ICON/Icon_StructureType.png" alt="" />
                  Go To Structure Report
                </Link>
              </div>
              <div className="col-12 col-md-6 col-xl-6 d-grid">
                <Link
                  to={`/admin/projects/${added.folderId}/summary`}
                  className="btn btn-outline-secondary m-2"
                >
                  <img className="btn-img me-1" src="/images/ICON/Icon_Arrows_Right.png" alt="" />
                  Go To Project Folder
                </Link>
              </div>
              <div className="col-12 col-md-6 col-xl-6 d-grid">
                <Link to="/newstructure" className="btn btn-outline-secondary m-2">
                  <img className="btn-img me-1" src="/images/ICON/Icon_Plus_Green_Square.png" alt="" />
                  Configure New Structure
                </Link>
              </div>
              <div className="col-12 col-md-6 col-xl-6 d-grid">
                <Link to="/" className="btn btn-outline-secondary m-2">
                  <img className="btn-img me-1" src="/images/ICON/Icon_Arrows_Left.png" alt="" />
                  Return to Main Menu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <h1>Add Structure To Project&nbsp;Folder</h1>
        </div>
      </div>

      <div className="row mt-lg-3 mt-1">
        <div className="col-12 col-xl-6">
          <h2>{s.nickname}</h2>
          <p className="mb-0">
            Style Number: {styleNumber}
            <br />
            Filter Type: {s.permanent ? 'Permanent' : 'Temporary'} | Oil Removal:{' '}
            {s.oilFiltration ? 'Yes' : 'No'}
          </p>
        </div>
        <div className="col-12 pt-2 pt-xl-0 col-xl-6">
          <div className="d-flex flex-wrap justify-content-center align-items-center">
            <div className="pe-2 fw-bold">
              Quantity To Add&nbsp;
            </div>
            <div>
              <input
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                type="number"
                min={1}
                className="form-control form-control-sm"
                style={{ width: 100 }}
                placeholder="Quantity"
                aria-label="Quantity to add"
              />
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div className="row mt-2">
          <div className="col-12">
            <div className="alert alert-info py-2 px-3 mb-0">{feedback}</div>
          </div>
        </div>
      )}

      <div className="row mt-lg-1 mt-1">
        <div className="col-sm-12">
          <div className="table-responsive">
            <table className="table w-100">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Status</th>
                  <th>Last Updated On</th>
                  <th>Structures</th>
                  <th style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => setCreateOpen((v) => !v)}
                    >
                      {createOpen ? 'Cancel' : 'Create New Project'}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {foldersQuery.isLoading && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Loading…
                    </td>
                  </tr>
                )}
                {!foldersQuery.isLoading && folders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      No project folders yet. Create one to get started.
                    </td>
                  </tr>
                )}
                {folders.map((f) => (
                  <tr key={f.id}>
                    <td style={{ verticalAlign: 'middle' }}>{f.name}</td>
                    <td style={{ verticalAlign: 'middle' }}>
                      {f.deliveredStatus ? (
                        <span className="badge bg-success">Delivered</span>
                      ) : f.completedStatus ? (
                        <span className="badge bg-info">Complete</span>
                      ) : (
                        <span className="badge bg-secondary">Draft</span>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>{formatDate(f.updatedAt)}</td>
                    <td style={{ verticalAlign: 'middle' }}>{f.structureCount}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        disabled={addMutation.isPending}
                        onClick={() => {
                          setFeedback(null);
                          addMutation.mutate(f.id);
                        }}
                      >
                        + Add To Project
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {createOpen && (
        <NewProjectForm
          pending={createMutation.isPending}
          error={
            createMutation.isError
              ? createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Could not create project.'
              : null
          }
          onCreate={(form) => createMutation.mutate(form)}
        />
      )}

      <div className="row">
        <div className="col align-self-center text-center">
          <Link
            to={`/structurereport/view/${id}`}
            className="btn btn-sm btn-secondary mt-2"
          >
            Go To Structure Report
          </Link>
        </div>
      </div>
    </div>
  );
}

function NewProjectForm({
  pending,
  error,
  onCreate,
}: {
  pending: boolean;
  error: string | null;
  onCreate: (form: ProjectFolderWrite) => void;
}) {
  const [form, setForm] = useState<ProjectFolderWrite>({ name: '', city: '', state: '' });

  const set = <K extends keyof ProjectFolderWrite>(k: K, v: ProjectFolderWrite[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h4 className="header-title mb-0">New Project</h4>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">City</label>
                <input
                  className="form-control"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">State</label>
                <input
                  className="form-control"
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3 d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!form.name.trim() || pending}
                onClick={() => onCreate(form)}
              >
                {pending ? 'Creating…' : 'Create & Add'}
              </button>
              {error && <div className="alert alert-danger mb-0 py-2 px-3">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
